import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AudioLines, Check } from 'lucide-react';
import { CHANNEL_PRESETS, CHANNEL_SILENT_DBFS, defaultSkipped, presetLabel } from '@castfeeds/domain';
import { useT } from '@/i18n';
import { ErrorBox, Ltr } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { qk } from '@/lib/api/keys';
import { useWs } from '@/lib/api/hooks';
import { logError, logInfo } from '@/lib/log';
import type { ChannelStat, SessionSource } from '@/lib/api/types';

interface Row {
  skip: boolean;
  person: string;
  label: string;
  /** The person typed the label: a preset change leaves it alone. */
  labelTouched: boolean;
}

/** The mics of one multitrack file: the pending source, or every source split from it (amendment 24). */
export function multitrackGroups(sources: SessionSource[]): SessionSource[][] {
  const groups = new Map<string, SessionSource[]>();
  for (const s of sources) {
    if (s.kind !== 'mic' || !s.multitrack) continue;
    const key = s.multitrack.group ?? s.id;
    groups.set(key, [...(groups.get(key) ?? []), s]);
  }
  return [...groups.values()].map((g) => [...g].sort((a, b) => (a.channel ?? -1) - (b.channel ?? -1)));
}

/** True while any multitrack mic of the session still needs a decision (split or mixdown). */
export const multitrackUndecided = (sources: SessionSource[]) => sources.some((s) => s.kind === 'mic' && s.multitrack?.pending);

/** RMS as a share of the meter (−60 dBFS … 0 dBFS). */
const meterShare = (db: number) => Math.max(0, Math.min(1, (db - CHANNEL_SILENT_DBFS) / -CHANNEL_SILENT_DBFS));

function LevelMeter({ stat }: { stat: ChannelStat }) {
  const { t } = useT();
  const n = stat.channel + 1;
  return (
    <div
      role="meter"
      aria-valuemin={CHANNEL_SILENT_DBFS}
      aria-valuemax={0}
      aria-valuenow={Math.max(CHANNEL_SILENT_DBFS, stat.rms_dbfs)}
      aria-label={t('mtLevelOf', { n, rms: stat.rms_dbfs, peak: stat.peak_dbfs })}
      title={t('mtLevelOf', { n, rms: stat.rms_dbfs, peak: stat.peak_dbfs })}
      dir="ltr"
      style={{ position: 'relative', height: 8, width: 120, borderRadius: 4, background: 'var(--line)' }}
    >
      <span style={{ position: 'absolute', insetInlineStart: 0, top: 0, bottom: 0, width: `${meterShare(stat.rms_dbfs) * 100}%`, borderRadius: 4, background: stat.silent ? 'var(--muted)' : 'var(--green)' }} />
      <span style={{ position: 'absolute', top: -2, bottom: -2, width: 2, insetInlineStart: `calc(${meterShare(stat.peak_dbfs) * 100}% - 1px)`, background: stat.peak_dbfs > -1 ? 'var(--accent-text)' : 'var(--ink)' }} />
    </div>
  );
}

function initialRows(group: SessionSource[], channels: number, stats: ChannelStat[] | null, preset: string, chLabel: (n: number) => string): Row[] {
  const split = group.filter((s) => s.channel !== null && s.channel !== undefined);
  return Array.from({ length: channels }, (_, c) => {
    const mine = split.find((s) => s.channel === c);
    if (split.length) return { skip: !mine, person: mine?.person ?? '', label: mine?.label || presetLabel(preset, c, channels) || chLabel(c + 1), labelTouched: !!mine };
    const st = stats?.find((x) => x.channel === c);
    return { skip: st ? defaultSkipped(st) : false, person: '', label: presetLabel(preset, c, channels) ?? chLabel(c + 1), labelTouched: false };
  });
}

function GroupPanel({ sessionId, group, editable, people }: { sessionId: string; group: SessionSource[]; editable: boolean; people: string[] }) {
  const { t } = useT();
  const qc = useQueryClient();
  const w = useWs();
  const head = group[0];
  const mt = head.multitrack!;
  const stats = mt.summary?.channels ?? null;
  const previews = useMemo(() => new Map(group.flatMap((s) => s.multitrack?.previews ?? []).map((p) => [p.channel, p.url])), [group]);
  const decided = !mt.pending;
  const [editing, setEditing] = useState(!decided);
  const [preset, setPreset] = useState('');
  const chLabel = (n: number) => t('mtChannelN', { n });
  const [rows, setRows] = useState<Row[]>(() => initialRows(group, mt.channels, stats, '', chLabel));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const groupKey = group.map((s) => `${s.id}:${s.channel ?? '-'}:${s.person}:${s.multitrack?.decision}`).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setRows(initialRows(group, mt.channels, stats, preset, chLabel)), [groupKey]);
  useEffect(() => setEditing(!decided), [decided]);

  const kept = rows.map((r, c) => ({ ...r, channel: c })).filter((r) => !r.skip);
  const missingPerson = kept.some((r) => !r.person.trim());
  const change = (c: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, i) => (i === c ? { ...r, ...patch } : r)));
  const pickPreset = (id: string) => {
    setPreset(id);
    setRows((rs) => rs.map((r, c) => (r.labelTouched ? r : { ...r, label: presetLabel(id, c, mt.channels) ?? chLabel(c + 1) })));
  };

  const submit = async (mixdown: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const body = mixdown ? ({ mixdown: true } as const) : { channels: kept.map((r) => ({ channel: r.channel, person_name: r.person.trim(), device_label: r.label.trim() })) };
      const r = await api.pipeline.splitSource(head.id, body);
      logInfo('upload', `multitrack ${r.decision} source=${head.id} group=${r.group} sources=${r.sources.length} changed=${r.changed}`);
      await qc.invalidateQueries({ queryKey: qk.session(w, sessionId) });
      setEditing(false);
    } catch (e) {
      logError('upload', `could not ${mixdown ? 'keep the mixdown of' : 'split'} source=${head.id}`, e);
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const listId = `mt-people-${head.id}`;
  if (!editing) {
    const split = group.filter((s) => s.channel !== null && s.channel !== undefined);
    return (
      <div className="card-row row wrap" style={{ padding: '10px 16px', gap: 10 }} data-testid="multitrack-done">
        <Check size={14} aria-hidden style={{ color: 'var(--green)' }} />
        <Ltr className="mono ellipsis"><span style={{ fontSize: 12 }}>{head.filename}</span></Ltr>
        <span className="small">
          {mt.decision === 'mixdown' ? t('mtDoneMixdown') : t('mtDoneSplit', { n: split.length, names: split.map((s) => `${s.person || s.label} (${t('mtChTag', { n: (s.channel ?? 0) + 1 })})`).join(', ') })}
        </span>
        {editable ? <button type="button" className="btn btn-xs push" onClick={() => setEditing(true)}>{t('mtChange')}</button> : null}
      </div>
    );
  }
  return (
    <div className="card-row" style={{ display: 'grid', gap: 10, padding: '12px 16px' }} data-testid="multitrack-panel">
      <div className="row wrap" style={{ gap: 10 }}>
        <AudioLines size={16} aria-hidden />
        <strong style={{ fontSize: 13 }}>{t('mtTitle', { file: head.filename, n: mt.channels })}</strong>
        <label className="row push" style={{ gap: 6, fontSize: 12 }}>
          <span className="muted">{t('mtPreset')}</span>
          <select className="select" style={{ height: 28 }} aria-label={t('mtPreset')} value={preset} onChange={(e) => pickPreset(e.target.value)} title={t('mtPresetHint')}>
            <option value="">{t('mtPresetNone')}</option>
            {CHANNEL_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      </div>
      <span className="muted small">{t('mtSub')}</span>
      {!stats ? <span className="small" style={{ color: 'var(--amber)' }}>{t('mtNoSummary')}</span> : null}
      <datalist id={listId}>{people.map((p) => <option key={p} value={p} />)}</datalist>
      <div className="table-wrap" style={{ overflowX: 'auto' }}>
        <table className="table" style={{ width: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              <th scope="col">{t('mtChannel')}</th>
              <th scope="col">{t('mtLevel')}</th>
              <th scope="col">{t('mtPreview')}</th>
              <th scope="col">{t('mtPerson')}</th>
              <th scope="col">{t('fileLabel')}</th>
              <th scope="col">{t('mtSkip')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, c) => {
              const st = stats?.find((x) => x.channel === c);
              const url = previews.get(c);
              return (
                <tr key={c} data-testid={`mt-row-${c}`} style={{ opacity: r.skip ? 0.55 : 1 }}>
                  <th scope="row" style={{ whiteSpace: 'nowrap' }}>
                    {t('mtChannelN', { n: c + 1 })}
                    {st?.silent ? <span className="tag" style={{ marginInlineStart: 6 }} title={t('mtSilentWhy', { rms: st.rms_dbfs })}>{t('mtSilent')}</span> : null}
                    {st && st.duplicate_of !== null ? <span className="tag" style={{ marginInlineStart: 6 }}>{t('mtDuplicate', { n: st.duplicate_of + 1 })}</span> : null}
                    {st?.mix ? <span className="tag" style={{ marginInlineStart: 6 }}>{t('mtMix')}</span> : null}
                  </th>
                  <td>
                    {st ? (
                      // Condition 4c: "Silent" is a hint; the measured level stays next to it so the operator sees why.
                      <span className="row" style={{ gap: 6 }}>
                        <LevelMeter stat={st} />
                        <Ltr className="mono"><span className="small muted" data-testid={`mt-level-${c}`}>{st.rms_dbfs} dBFS</span></Ltr>
                      </span>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td>
                    {url ? (
                      <audio controls preload="none" src={url} aria-label={t('mtPreviewOf', { n: c + 1 })} style={{ height: 28, width: 180 }} dir="ltr" />
                    ) : (
                      <span className="muted small">{head.local ? t('mtNoPreview') : '—'}</span>
                    )}
                  </td>
                  <td>
                    <input className="input" style={{ height: 28, minWidth: 130 }} list={listId} aria-label={`${t('mtPerson')} · ${t('mtChannelN', { n: c + 1 })}`} placeholder={t('filePersonPh')} disabled={r.skip || busy} value={r.person} onChange={(e) => change(c, { person: e.target.value })} />
                  </td>
                  <td>
                    <input className="input" style={{ height: 28, minWidth: 130 }} aria-label={`${t('fileLabel')} · ${t('mtChannelN', { n: c + 1 })}`} disabled={r.skip || busy} value={r.label} onChange={(e) => change(c, { label: e.target.value, labelTouched: true })} />
                  </td>
                  <td>
                    <input type="checkbox" aria-label={`${t('mtSkip')} · ${t('mtChannelN', { n: c + 1 })}`} checked={r.skip} disabled={busy} onChange={(e) => change(c, { skip: e.target.checked })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {error ? <ErrorBox error={error} /> : null}
      <div className="row wrap" style={{ gap: 8 }}>
        {kept.length === 0 ? <span className="small" style={{ color: 'var(--amber)' }}>{t('mtNeedOne')}</span> : missingPerson ? <span className="small" style={{ color: 'var(--amber)' }}>{t('mtNeedPerson')}</span> : null}
        <button type="button" className="btn btn-sm push" disabled={busy || !editable} onClick={() => void submit(true)} title={t('mtMixdownHint')}>{t('mtMixdown')}</button>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy || !editable || kept.length === 0 || missingPerson} onClick={() => void submit(false)}>
          {busy ? t('mtSplitting') : t('mtSplit', { n: kept.length })}
        </button>
      </div>
    </div>
  );
}

/** The channel tables of every multitrack mic upload of a session (amendment 24). */
export function MultitrackPanel({ sessionId, sources, editable }: { sessionId: string; sources: SessionSource[]; editable: boolean }) {
  const groups = multitrackGroups(sources);
  const people = useMemo(() => [...new Set(sources.flatMap((s) => [s.person, ...(s.people ?? [])]).filter((p): p is string => !!p && !!p.trim()))], [sources]);
  if (!groups.length) return null;
  return (
    <section className="card" aria-label="multitrack">
      {groups.map((g) => <GroupPanel key={g[0].multitrack?.group ?? g[0].id} sessionId={sessionId} group={g} editable={editable} people={people} />)}
    </section>
  );
}
